import type { EvidenceCapsuleRef } from "@cogo/schema/productization-spine";
import {
  STUDIO_FEATURED_OUTCOME_SOURCE,
  isStudioOutcomeSkuId,
  type StudioOutcomeSkuId,
} from "./studio-outcome-catalog";

export {
  STUDIO_FEATURED_OUTCOME_SOURCE,
  STUDIO_NEWSLETTER_MEMBERSHIP_SKU_ID,
  STUDIO_RESERVATION_OPERATIONS_SKU_ID,
} from "./studio-outcome-catalog";

export const STUDIO_CANDIDATE_ENTRY_EVIDENCE_VERSION =
  "cogo.studio-candidate-entry-evidence.v1" as const;

export type StudioCandidateEntryEvidenceRole = "demand-signal" | "preview-plan";

interface StudioCandidateEntryEvidenceBase {
  schemaVersion: typeof STUDIO_CANDIDATE_ENTRY_EVIDENCE_VERSION;
  role: StudioCandidateEntryEvidenceRole;
  projectId: string;
  skuId: StudioOutcomeSkuId;
  candidateId: string;
}

export interface StudioCandidateDemandSignalEvidence extends StudioCandidateEntryEvidenceBase {
  role: "demand-signal";
  payload: {
    selectedOutcome: StudioOutcomeSkuId;
    source: typeof STUDIO_FEATURED_OUTCOME_SOURCE;
  };
}

export interface StudioCandidatePreviewPlanEvidence extends StudioCandidateEntryEvidenceBase {
  role: "preview-plan";
  payload: {
    appName: string;
    appClass: string;
    targetPlatform: string;
    planningSource: string;
    pages: string[];
    blocks: string[];
  };
}

export type StudioCandidateEntryEvidence =
  | StudioCandidateDemandSignalEvidence
  | StudioCandidatePreviewPlanEvidence;

export interface StudioCandidateEntryEvidenceBundle {
  demandSignal: StudioCandidateDemandSignalEvidence;
  demandSignalRef: EvidenceCapsuleRef;
  previewPlan: StudioCandidatePreviewPlanEvidence;
  previewPlanRef: EvidenceCapsuleRef;
}

const IDENTIFIER_LIMIT = 200;
const LIST_LIMIT = 128;
const LIST_ITEM_LIMIT = 400;
const SHA256_PATTERN = /^sha256:[a-f0-9]{64}$/;
const CONTROL_CHARACTER_PATTERN = /[\u0000-\u001f\u007f-\u009f]/;

function record(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function exactKeys(
  value: Record<string, unknown>,
  expected: string[],
): boolean {
  return (
    Object.keys(value).sort().join("\0") === [...expected].sort().join("\0")
  );
}

function boundedText(value: unknown, limit: number): value is string {
  return (
    typeof value === "string" &&
    value.trim().length > 0 &&
    value.length <= limit &&
    !CONTROL_CHARACTER_PATTERN.test(value)
  );
}

function boundedIdentifier(value: unknown): value is string {
  return boundedText(value, IDENTIFIER_LIMIT);
}

function boundedTextList(value: unknown): value is string[] {
  return (
    Array.isArray(value) &&
    value.length <= LIST_LIMIT &&
    value.every((item) => boundedText(item, LIST_ITEM_LIMIT))
  );
}

export function canonicalizeCandidateEntryEvidence(value: unknown): string {
  if (value === null) return "null";
  if (typeof value === "string" || typeof value === "boolean") {
    return JSON.stringify(value);
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return `[${value
      .map((item) => canonicalizeCandidateEntryEvidence(item))
      .join(",")}]`;
  }
  const object = record(value);
  if (object) {
    return `{${Object.keys(object)
      .sort()
      .map(
        (key) =>
          `${JSON.stringify(key)}:${canonicalizeCandidateEntryEvidence(object[key])}`,
      )
      .join(",")}}`;
  }
  throw new Error("candidate_entry_evidence_not_canonical_json");
}

async function sha256(value: string): Promise<`sha256:${string}`> {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(value),
  );
  return `sha256:${[...new Uint8Array(digest)]
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("")}`;
}

async function evidenceRef(
  artifact: StudioCandidateEntryEvidence,
): Promise<EvidenceCapsuleRef> {
  return {
    ref: `${artifact.role}:studio:${artifact.candidateId}`,
    contentHash: await sha256(canonicalizeCandidateEntryEvidence(artifact)),
  };
}

export async function createStudioCandidateEntryEvidence(input: {
  projectId: string;
  skuId: string;
  candidateId: string;
  appName: string;
  appClass: string;
  targetPlatform: string;
  planningSource: string;
  pages: string[];
  blocks: string[];
}): Promise<StudioCandidateEntryEvidenceBundle> {
  if (!isStudioOutcomeSkuId(input.skuId)) {
    throw new Error("candidate_entry_evidence_skuId_invalid");
  }
  for (const [label, value] of [
    ["projectId", input.projectId],
    ["candidateId", input.candidateId],
  ] as const) {
    if (!boundedIdentifier(value)) {
      throw new Error(`candidate_entry_evidence_${label}_invalid`);
    }
  }
  for (const [label, value] of [
    ["appName", input.appName],
    ["appClass", input.appClass],
    ["targetPlatform", input.targetPlatform],
    ["planningSource", input.planningSource],
  ] as const) {
    if (!boundedText(value, LIST_ITEM_LIMIT)) {
      throw new Error(`candidate_entry_evidence_${label}_invalid`);
    }
  }
  if (!boundedTextList(input.pages) || !boundedTextList(input.blocks)) {
    throw new Error("candidate_entry_evidence_structure_invalid");
  }

  const demandSignal: StudioCandidateDemandSignalEvidence = {
    schemaVersion: STUDIO_CANDIDATE_ENTRY_EVIDENCE_VERSION,
    role: "demand-signal",
    projectId: input.projectId,
    skuId: input.skuId,
    candidateId: input.candidateId,
    payload: {
      selectedOutcome: input.skuId,
      source: STUDIO_FEATURED_OUTCOME_SOURCE,
    },
  };
  const previewPlan: StudioCandidatePreviewPlanEvidence = {
    schemaVersion: STUDIO_CANDIDATE_ENTRY_EVIDENCE_VERSION,
    role: "preview-plan",
    projectId: input.projectId,
    skuId: input.skuId,
    candidateId: input.candidateId,
    payload: {
      appName: input.appName.trim(),
      appClass: input.appClass.trim(),
      targetPlatform: input.targetPlatform.trim(),
      planningSource: input.planningSource.trim(),
      pages: [...input.pages],
      blocks: [...input.blocks],
    },
  };
  return {
    demandSignal,
    demandSignalRef: await evidenceRef(demandSignal),
    previewPlan,
    previewPlanRef: await evidenceRef(previewPlan),
  };
}

export async function assertStudioCandidateEntryEvidenceBinding(input: {
  artifact: unknown;
  ref: unknown;
  role: StudioCandidateEntryEvidenceRole;
  projectId: string;
  skuId: string;
  candidateId: string;
}): Promise<void> {
  const artifact = record(input.artifact);
  const payload = record(artifact?.payload);
  const ref = record(input.ref);
  if (
    !artifact ||
    !payload ||
    !ref ||
    !isStudioOutcomeSkuId(input.skuId) ||
    !exactKeys(artifact, [
      "schemaVersion",
      "role",
      "projectId",
      "skuId",
      "candidateId",
      "payload",
    ]) ||
    artifact.schemaVersion !== STUDIO_CANDIDATE_ENTRY_EVIDENCE_VERSION ||
    artifact.role !== input.role ||
    artifact.projectId !== input.projectId ||
    artifact.skuId !== input.skuId ||
    artifact.candidateId !== input.candidateId ||
    !exactKeys(ref, ["ref", "contentHash"]) ||
    ref.ref !== `${input.role}:studio:${input.candidateId}` ||
    typeof ref.contentHash !== "string" ||
    !SHA256_PATTERN.test(ref.contentHash)
  ) {
    throw new Error("candidate_entry_evidence_binding_invalid");
  }
  if (input.role === "demand-signal") {
    if (
      !exactKeys(payload, ["selectedOutcome", "source"]) ||
      payload.selectedOutcome !== input.skuId ||
      payload.source !== STUDIO_FEATURED_OUTCOME_SOURCE
    ) {
      throw new Error("candidate_entry_evidence_payload_invalid");
    }
  } else if (
    !exactKeys(payload, [
      "appName",
      "appClass",
      "targetPlatform",
      "planningSource",
      "pages",
      "blocks",
    ]) ||
    !boundedText(payload.appName, LIST_ITEM_LIMIT) ||
    !boundedText(payload.appClass, LIST_ITEM_LIMIT) ||
    !boundedText(payload.targetPlatform, LIST_ITEM_LIMIT) ||
    !boundedText(payload.planningSource, LIST_ITEM_LIMIT) ||
    !boundedTextList(payload.pages) ||
    !boundedTextList(payload.blocks)
  ) {
    throw new Error("candidate_entry_evidence_payload_invalid");
  }
  const expectedHash = await sha256(
    canonicalizeCandidateEntryEvidence(artifact),
  );
  if (ref.contentHash !== expectedHash) {
    throw new Error("candidate_entry_evidence_hash_invalid");
  }
}
